import { useCallback, useEffect } from 'react';
import type React from 'react';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useSignatureTracking } from '../../hooks/tracking/signature';
import { Button } from '../signForm/components/form/sunriseStyleWrappers/Button';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import { useMobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaIcon } from './SignCtaIcon';

type Props = {
	sticky: boolean;
	visible: boolean;
};

export const MobileSignButtonContainer: React.FC<Props> = ({ sticky, visible }) => {
	const { trackMobileSignIntent, trackClosedModal } = useSignatureTracking();

	const context = useMobileModalContext();
	const openModal = useMobileSignModal();

	usePreloadMobileSignModal();

	const handleModalWithoutTracking = useCallback(async () => {
		await openModal(context).waitForClosed();
	}, [context, openModal]);

	const handleModal = useCallback(async () => {
		trackMobileSignIntent();
		await openModal(context).waitForClosed();
		trackClosedModal();
	}, [context, openModal, trackMobileSignIntent, trackClosedModal]);

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			<ClientRender>
				{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
				<MobileSignButtonAutoOpen onOpen={handleModalWithoutTracking} />
			</ClientRender>
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				<Button
					variant="primary"
					onClick={handleModal}
					leadingIcon={<SignCtaIcon width="24px" height="24px" />}
					data-qa="mobile-sign-button"
					data-testid="mobile-sign-button"
					data-gtm="ga4-sign-button"
					size="large"
					className="w-full max-w-none"
				>
					<div className="flex place-content-center place-items-center gap-3">
						<Translate value="corgi.petition_gamma.petition_sign.action" />
					</div>
				</Button>
			</ActionButtonWrapper>
		</>
	);
};

function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	// FIXME this should be moved somewhere else as this means that rendering MobileSignButtonContainer twice
	// will result in trying to open the modal twice
	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
